import { useState, useEffect } from 'react';
import { apiRequest } from '../../platform/api';
import {
  Users,
  FileText,
  TrendingUp,
  Database,
  Loader2,
  BarChart3,
  UserPlus,
  Clock,
  AlertTriangle,
} from 'lucide-react';

interface AnalyticsData {
  overview: {
    total_users: number;
    signups_7d: number;
    signups_30d: number;
    active_users_7d: number;
    active_users_30d: number;
    total_posts: number;
    posts_7d: number;
    posts_30d: number;
    total_sources: number;
    sources_7d: number;
  };
  trials: {
    active: number;
    expired: number;
  };
  plan_distribution: Record<string, number>;
  daily_activity: Record<string, { posts: number; sources: number }>;
  top_users: Array<{ email: string; posts: number }>;
  source_types: Record<string, number>;
  weekly_signups: Array<{ week: string; paid: number; free: number }>;
}

export function AdminAnalyticsPage() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setLoading(true);
        const result = await apiRequest<{ data: AnalyticsData }>('/api/admin/analytics');
        const analyticsData = (result as any).data || result;
        setData(analyticsData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    loadAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { overview, trials, plan_distribution, daily_activity, top_users, source_types, weekly_signups } = data;

  // Get last 14 days for the activity chart
  const last14Days: string[] = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    last14Days.push(d.toISOString().substring(0, 10));
  }

  const maxDailyPosts = Math.max(
    1,
    ...last14Days.map((d) => daily_activity[d]?.posts || 0)
  );

  return (
    <div className="p-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
        <p className="text-gray-500 mt-2">Monitor usage and growth</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total Users"
          value={overview.total_users}
          subtitle={`+${overview.signups_7d} this week`}
          icon={Users}
          color="purple"
        />
        <StatCard
          label="Active Users (7d)"
          value={overview.active_users_7d}
          subtitle={`${overview.active_users_30d} in last 30d`}
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          label="Posts Generated"
          value={overview.total_posts}
          subtitle={`+${overview.posts_7d} this week`}
          icon={FileText}
          color="blue"
        />
        <StatCard
          label="Sources Connected"
          value={overview.total_sources}
          subtitle={`+${overview.sources_7d} this week`}
          icon={Database}
          color="orange"
        />
      </div>

      {/* Second Row: Trials + Plan Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Trials */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-5 w-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">Trials</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-900">{trials.active}</div>
              <div className="text-sm text-blue-600">Active trials</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <div className="flex items-center gap-1.5">
                <div className="text-2xl font-bold text-amber-900">{trials.expired}</div>
                {trials.expired > 0 && <AlertTriangle className="h-4 w-4 text-amber-500" />}
              </div>
              <div className="text-sm text-amber-600">Expired (not converted)</div>
            </div>
          </div>
        </div>

        {/* Plan Distribution */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-5 w-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">Plan Distribution</h2>
          </div>
          {Object.keys(plan_distribution).length === 0 ? (
            <p className="text-sm text-gray-500">No subscription data yet</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(plan_distribution)
                .sort(([, a], [, b]) => b - a)
                .map(([plan, count]) => {
                  const total = Object.values(plan_distribution).reduce((s, v) => s + v, 0);
                  const pct = total > 0 ? (count / total) * 100 : 0;
                  return (
                    <div key={plan}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700 capitalize">{plan}</span>
                        <span className="text-gray-500">{count} ({pct.toFixed(0)}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-purple-500 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Daily Activity Chart (simple bar chart) */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Daily Activity (Last 14 Days)</h2>
        </div>
        <div className="flex items-end gap-1.5 h-40">
          {last14Days.map((day) => {
            const posts = daily_activity[day]?.posts || 0;
            const height = maxDailyPosts > 0 ? (posts / maxDailyPosts) * 100 : 0;
            const dayLabel = new Date(day + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
            return (
              <div key={day} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-gray-500 font-medium">{posts || ''}</span>
                <div className="w-full bg-gray-100 rounded-t flex-1 relative" style={{ minHeight: '4px' }}>
                  <div
                    className="absolute bottom-0 w-full bg-purple-500 rounded-t transition-all"
                    style={{ height: `${Math.max(height, posts > 0 ? 4 : 0)}%` }}
                  />
                </div>
                <span className="text-[10px] text-gray-400 whitespace-nowrap">{dayLabel}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 bg-purple-500 rounded" /> Posts generated
          </span>
        </div>
      </div>

      {/* Weekly New Users (stacked bar chart) */}
      {(() => {
        const maxWeekly = Math.max(1, ...weekly_signups.map((w) => w.paid + w.free));
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <UserPlus className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">New Users (Last 12 Weeks)</h2>
            </div>
            <div className="flex items-end gap-1.5 h-40">
              {weekly_signups.map((w) => {
                const total = w.paid + w.free;
                const totalHeight = maxWeekly > 0 ? (total / maxWeekly) * 100 : 0;
                const paidPct = total > 0 ? (w.paid / total) * 100 : 0;
                const weekLabel = new Date(w.week + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                return (
                  <div key={w.week} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-500 font-medium">{total || ''}</span>
                    <div className="w-full bg-gray-100 rounded-t flex-1 relative" style={{ minHeight: '4px' }}>
                      <div
                        className="absolute bottom-0 w-full rounded-t overflow-hidden flex flex-col justify-end"
                        style={{ height: `${Math.max(totalHeight, total > 0 ? 4 : 0)}%` }}
                      >
                        <div className="w-full bg-green-500" style={{ height: `${paidPct}%` }} />
                        <div className="w-full bg-blue-400" style={{ height: `${100 - paidPct}%` }} />
                      </div>
                    </div>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{weekLabel}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 bg-green-500 rounded" /> Paid
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 bg-blue-400 rounded" /> Free
              </span>
            </div>
          </div>
        );
      })()}

      {/* Bottom Row: Top Users + Source Types */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Users */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="h-5 w-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">Top Users (30d)</h2>
          </div>
          {top_users.length === 0 ? (
            <p className="text-sm text-gray-500">No activity yet</p>
          ) : (
            <div className="space-y-2">
              {top_users.map((u, i) => (
                <div key={u.email} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-gray-400 w-5">{i + 1}</span>
                    <span className="text-sm text-gray-700 truncate max-w-[200px]">{u.email}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{u.posts} posts</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Source Types */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Database className="h-5 w-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">Source Types</h2>
          </div>
          {Object.keys(source_types).length === 0 ? (
            <p className="text-sm text-gray-500">No sources yet</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(source_types)
                .sort(([, a], [, b]) => b - a)
                .map(([type, count]) => {
                  const total = Object.values(source_types).reduce((s, v) => s + v, 0);
                  const pct = total > 0 ? (count / total) * 100 : 0;
                  return (
                    <div key={type}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700 capitalize">{type.replace(/_/g, ' ')}</span>
                        <span className="text-gray-500">{count}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  subtitle,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  subtitle: string;
  icon: any;
  color: string;
}) {
  const bgColor = {
    purple: 'bg-purple-100',
    green: 'bg-green-100',
    blue: 'bg-blue-100',
    orange: 'bg-orange-100',
  }[color] || 'bg-gray-100';

  const iconColor = {
    purple: 'text-purple-600',
    green: 'text-green-600',
    blue: 'text-blue-600',
    orange: 'text-orange-600',
  }[color] || 'text-gray-600';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-gray-500 mb-1">{label}</div>
          <div className="text-3xl font-bold text-gray-900">{value.toLocaleString()}</div>
          <div className="text-xs text-gray-400 mt-1">{subtitle}</div>
        </div>
        <div className={`p-3 ${bgColor} rounded-lg`}>
          <Icon className={`h-6 w-6 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

export default AdminAnalyticsPage;
